import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const EventTile = ({ event, onClick }) => {
    // Parse Dates
    const start = event.start.dateTime ? new Date(event.start.dateTime) : new Date(event.start.date);
    const end = event.end.dateTime ? new Date(event.end.dateTime) : new Date(event.end.date);
    const isAllDay = !event.start.dateTime;

    // Formatting
    const timeStr = isAllDay 
        ? 'All Day' 
        : start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    
    // Check if event is in the past
    // For all-day events, we compare against the end of the day (effectively midnight of the next day)
    // For regular events, we use the end time.
    const now = new Date();
    const isPast = end < now;

    // Use Calendar Color or Fallback
    const bgColor = event.calendarColor || '#4fd1c5'; // Default teal if no color

    return (
        <Paper
            elevation={4}
            sx={{
                p: 2,
                minHeight: '165px', // Reduced height (~2/3 of 250px)
                flexShrink: 0,      
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                bgcolor: bgColor,
                color: 'white',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                opacity: isPast ? 0.6 : 1, // Dim past events
                filter: isPast ? 'grayscale(40%)' : 'none', // Slightly desaturate past events
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                    opacity: 1, // Restore opacity on hover
                    filter: 'none' // Remove grayscale on hover
                },
                position: 'relative',
                overflow: 'hidden'
            }}
            onClick={() => onClick(event)}
        >
            {/* Gloss Effect Overlay */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '40%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%)',
                pointerEvents: 'none'
            }} />

            <Box>
                {/* Calendar Label Badge */}
                <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ 
                        bgcolor: 'rgba(0,0,0,0.15)', 
                        color: 'rgba(255,255,255,0.9)',
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.05em'
                    }}>

                        {(() => {
                            const summary = event.calendarSummary;
                            if (summary === 'rdymock@gmail.com') return 'Dad';
                            if (summary === 'leahdymock@gmail.com') return 'Mom';
                            return summary;
                        })()}
                    </Typography>
                </Box>

                <Typography variant="h5" fontWeight="bold" sx={{ 
                    lineHeight: 1.2, 
                    mb: 1,
                    textShadow: '0px 2px 4px rgba(0,0,0,0.1)' 
                }}>
                    {event.summary}
                </Typography>
            </Box>

            <Box>
                <Typography variant="h4" fontWeight="900" sx={{ opacity: 0.95, letterSpacing: '-0.02em', mb: 0.5 }}>
                    {timeStr}
                </Typography>
                
                {event.location && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <LocationOnIcon fontSize="small" sx={{ opacity: 0.9 }} />
                        <Typography variant="body2" noWrap sx={{ opacity: 0.9, maxWidth: '90%' }}>
                            {event.location}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default EventTile;
